import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api, getErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import BedeeSearchField from "@/components/BedeeSearchField";
import { formatFileSize } from "@/lib/format";
import {
  Folder, FolderOpen, FileText, ChevronRight, ChevronDown,
  HardDrive, Home, Download, Trash2, X, AlertTriangle, Loader2, Search, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

export default function Docs() {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPaths, setOpenPaths] = useState(new Set(["/"]));
  const [selectedPath, setSelectedPath] = useState("/");
  const [selectedFile, setSelectedFile] = useState(null); // { id, filename, type, classified_at }
  const [signedUrl, setSignedUrl] = useState(null);
  const [urlLoading, setUrlLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false); // file to confirm delete
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Search
  const [tokens, setTokens] = useState([]);
  const [draft, setDraft] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const isSearching = tokens.length > 0 || draft.trim().length > 0;

  // Debounced search
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const allTokens = draft.trim() ? [...tokens, draft.trim()] : tokens;
    if (allTokens.length === 0) { setSearchResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      const params = new URLSearchParams();
      allTokens.forEach((t) => params.append("q", t));
      api.get(`/documents/search?${params.toString()}`)
        .then((r) => setSearchResults(r.data))
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [tokens, draft]);

  const addToken = (val) => {
    const v = val.trim();
    if (v && !tokens.includes(v)) setTokens((prev) => [...prev, v]);
    setDraft("");
  };
  const removeToken = (i) => setTokens((prev) => prev.filter((_, idx) => idx !== i));
  const clearSearch = () => { setTokens([]); setDraft(""); setSearchResults([]); };
  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === " ") && draft.trim()) { e.preventDefault(); addToken(draft); }
    else if (e.key === "Backspace" && !draft && tokens.length > 0) setTokens((prev) => prev.slice(0, -1));
  };

  const loadTree = useCallback(() => {
    setLoading(true);
    api.get("/documents/tree").then((r) => {
      setTree(r.data);
      const paths = new Set(["/"]);
      const walk = (node, p, depth) => {
        if (depth < 2) paths.add(p);
        (node.children || []).forEach((c) => walk(c, p + c.name + "/", depth + 1));
      };
      if (r.data) walk(r.data, "/", 0);
      setOpenPaths(paths);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadTree(); }, [loadTree]);

  const toggle = (p) => {
    setOpenPaths((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };

  // Sociétés pour les dropdowns
  const [societes, setSocietes] = useState([]);
  useEffect(() => { api.get("/societes").then((r) => setSocietes(r.data)).catch(() => {}); }, []);

  // Entreprise sélectionnée dans le formulaire (pour filtrer les contacts)
  const [activeEntrepriseId, setActiveEntrepriseId] = useState(null);

  // Metadata editing state
  const [docDetails, setDocDetails] = useState(null);   // full doc with metadata
  const [typeFields, setTypeFields] = useState([]);     // fields from document type
  const [editMeta, setEditMeta] = useState({});         // current edits
  const [savingMeta, setSavingMeta] = useState(false);

  // Reclassification state
  const [reclassifyMode, setReclassifyMode] = useState(false);
  const [allTypes, setAllTypes] = useState([]);
  const [reclassTypeId, setReclassTypeId] = useState("");
  const [reclassTypeSearch, setReclassTypeSearch] = useState("");
  const [reclassTypeOpen, setReclassTypeOpen] = useState(false);
  const reclassTypeRef = useRef(null);

  useEffect(() => {
    api.get("/document-types").then((r) => setAllTypes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (reclassTypeRef.current && !reclassTypeRef.current.contains(e.target))
        setReclassTypeOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectFile = (d) => {
    setSelectedFile(d);
    setSignedUrl(null);
    setUrlLoading(true);
    setDocDetails(null);
    setEditMeta({});
    setTypeFields([]);
    setActiveEntrepriseId(null);
    // Load signed URL
    api.get(`/documents/${d.id}/signed-url`)
      .then((r) => setSignedUrl(r.data.url))
      .catch(() => toast.error("Impossible de charger l'aperçu."))
      .finally(() => setUrlLoading(false));
    // Load full document details + type fields in parallel
    api.get(`/documents/${d.id}`).then((r) => {
      setDocDetails(r.data);
      setEditMeta(r.data.metadata || {});
      if (r.data.document_type_id) {
        api.get(`/document-types/${r.data.document_type_id}`).then((rt) => {
          setTypeFields(rt.data.fields || []);
        }).catch(() => {});
      }
    }).catch(() => {});
  };

  const saveMetadata = async () => {
    if (!selectedFile) return;
    setSavingMeta(true);
    try {
      const typeId = reclassifyMode && reclassTypeId
        ? reclassTypeId
        : docDetails?.document_type_id;
      const r = await api.patch(`/documents/${selectedFile.id}/metadata`, {
        document_type_id: typeId,
        metadata: editMeta,
      });
      toast.success(
        reclassifyMode
          ? `Reclassifié → ${r.data.classified_filename || "OK"}`
          : `Renommé → ${r.data.classified_filename || "Enregistré"}`
      );
      setDocDetails(r.data);
      setEditMeta(r.data.metadata || {});
      setSelectedFile((prev) => ({
        ...prev,
        filename: r.data.classified_filename || prev.filename,
        classified_at: r.data.classified_at,
      }));
      setReclassifyMode(false);
      setReclassTypeId("");
      setReclassTypeSearch("");
      loadTree();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSavingMeta(false);
    }
  };

  const startReclassify = () => {
    setReclassifyMode(true);
    setReclassTypeId(docDetails?.document_type_id || "");
    setReclassTypeSearch("");
    setEditMeta({});
    setTypeFields([]);
  };

  const selectReclassType = (t) => {
    setReclassTypeId(t.id);
    setReclassTypeSearch(t.name);
    setReclassTypeOpen(false);
    setEditMeta({});
    api.get(`/document-types/${t.id}`).then((r) => setTypeFields(r.data.fields || [])).catch(() => {});
  };

  const closePreview = () => {
    setSelectedFile(null); setSignedUrl(null); setLightboxOpen(false);
    setDocDetails(null); setTypeFields([]); setEditMeta({});
    setReclassifyMode(false); setReclassTypeId(""); setReclassTypeSearch("");
  };

  const downloadFile = () => {
    if (!signedUrl || !selectedFile) return;
    const a = document.createElement("a");
    a.href = signedUrl;
    a.download = selectedFile.filename;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/documents/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.filename}" supprimé.`);
      setDeleteTarget(null);
      if (selectedFile?.id === deleteTarget.id) closePreview();
      loadTree();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // Find node by path
  const nodeAtPath = useMemo(() => {
    if (!tree) return null;
    if (selectedPath === "/") return tree;
    const segments = selectedPath.split("/").filter(Boolean);
    let node = tree;
    for (const s of segments) {
      const next = (node.children || []).find((c) => c.name === s);
      if (!next) return null;
      node = next;
    }
    return node;
  }, [tree, selectedPath]);

  const breadcrumbs = useMemo(() => {
    const parts = selectedPath.split("/").filter(Boolean);
    const crumbs = [{ name: "Racine", path: "/" }];
    let acc = "/";
    for (const p of parts) {
      acc += p + "/";
      crumbs.push({ name: p, path: acc });
    }
    return crumbs;
  }, [selectedPath]);

  const hasContent = tree && ((tree.children?.length || 0) > 0 || (tree.documents?.length || 0) > 0);

  const renderTreeRows = (node, path, level) => {
    const rows = [];
    const isRoot = level === 0;
    const isOpen = isRoot || openPaths.has(path);
    const childCount = (node.children?.length || 0) + (node.documents?.length || 0);
    const isSelected = selectedPath === (isRoot ? "/" : path);

    if (!isRoot) {
      const hasChildren = (node.children?.length || 0) > 0;
      rows.push(
        <div
          key={`f:${path}`}
          data-testid={`tree-folder-${node.name}`}
          onClick={() => setSelectedPath(path)}
          onDoubleClick={() => toggle(path)}
          className={`group flex items-center gap-1 py-1 px-1 cursor-pointer text-[13px] select-none transition-colors ${
            isSelected ? "bg-[#cbe3ff] text-zinc-900" : "hover:bg-zinc-100 text-zinc-800"
          }`}
          style={{ paddingLeft: `${(level - 1) * 14 + 4}px` }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); toggle(path); }}
            className="w-4 h-4 flex items-center justify-center text-zinc-500 hover:text-zinc-900 shrink-0"
            tabIndex={-1}
          >
            {hasChildren ? (
              isOpen ? <ChevronDown size={12} strokeWidth={2} /> : <ChevronRight size={12} strokeWidth={2} />
            ) : null}
          </button>
          {isOpen ? (
            <FolderOpen size={14} strokeWidth={1.5} className="text-[#FFC100] fill-[#FFE082] shrink-0" />
          ) : (
            <Folder size={14} strokeWidth={1.5} className="text-[#FFC100] fill-[#FFE082] shrink-0" />
          )}
          <span className="truncate">{node.name}</span>
        </div>
      );
    } else {
      // Root row
      rows.push(
        <div
          key="root"
          onClick={() => setSelectedPath("/")}
          className={`flex items-center gap-1 py-1 px-1 cursor-pointer text-[13px] select-none transition-colors ${
            selectedPath === "/" ? "bg-[#cbe3ff] text-zinc-900" : "hover:bg-zinc-100 text-zinc-800"
          }`}
        >
          <span className="w-4" />
          <HardDrive size={14} strokeWidth={1.5} className="text-zinc-500 shrink-0" />
          <span className="font-medium">Documents classés</span>
          <span className="ml-auto text-[10px] text-zinc-400 font-mono">{childCount}</span>
        </div>
      );
    }

    if (isOpen) {
      (node.children || []).forEach((child) => {
        const childPath = (isRoot ? "/" : path) + child.name + "/";
        rows.push(...renderTreeRows(child, childPath, level + 1));
      });
    }
    return rows;
  };

  const filesInCurrent = nodeAtPath?.documents || [];
  const subFolders = nodeAtPath?.children || [];

  return (
    <div className="min-h-screen flex flex-col">
      <PageHeader
        title="Documents"
        subtitle="Explorateur des documents classés"
        index="04"
      />

      {/* Tokenized search bar */}
      <div
        data-testid="docs-search-bar"
        className="border-b border-zinc-200 bg-white px-6 py-2.5 flex items-center gap-2 cursor-text"
        onClick={() => searchRef.current?.focus()}
      >
        {searching
          ? <Loader2 size={14} strokeWidth={1.5} className="text-zinc-400 animate-spin shrink-0" />
          : <Search size={14} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
        }
        <div className="flex items-center gap-1.5 flex-wrap flex-1">
          {tokens.map((t, i) => (
            <span key={i} data-testid={`search-token-${i}`}
              className="inline-flex items-center gap-1 bg-[#002FA7] text-white text-[11px] font-mono px-2 py-0.5 shrink-0">
              {t}
              <button onClick={(e) => { e.stopPropagation(); removeToken(i); }}
                className="opacity-70 hover:opacity-100 transition-opacity">
                <X size={9} strokeWidth={2.5} />
              </button>
            </span>
          ))}
          <input
            ref={searchRef}
            data-testid="docs-search-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => { if (draft.trim()) addToken(draft); }}
            placeholder={tokens.length === 0 ? "Rechercher par nom, chemin, métadonnée…  (Espace ou Entrée pour valider)" : "Ajouter un terme…"}
            className="flex-1 min-w-[160px] text-sm bg-transparent outline-none text-zinc-800 placeholder:text-zinc-400"
          />
        </div>
        {isSearching && (
          <button data-testid="docs-search-clear" onClick={clearSearch}
            className="text-zinc-400 hover:text-zinc-700 transition-colors shrink-0">
            <X size={14} strokeWidth={1.5} />
          </button>
        )}
      </div>

      {/* Address / breadcrumb bar — masqué en mode recherche */}
      {!isSearching && (
        <div className="border-b border-zinc-200 bg-zinc-50/50 px-6 py-2 flex items-center gap-1 text-xs font-mono">
        <Home size={12} strokeWidth={1.5} className="text-zinc-400" />
        {breadcrumbs.map((c, i) => (
          <React.Fragment key={c.path}>
            <button
              onClick={() => setSelectedPath(c.path)}
              data-testid={`crumb-${i}`}
              className="px-1 py-0.5 hover:bg-zinc-200 text-zinc-700 hover:text-zinc-900 transition-colors"
            >
              {c.name}
            </button>
            {i < breadcrumbs.length - 1 && <ChevronRight size={10} strokeWidth={1.5} className="text-zinc-400" />}
          </React.Fragment>
        ))}
      </div>
      )}

      {/* Zone principale : arbre + contenu central + panneau preview */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* Colonne gauche : arbre des dossiers — visible uniquement hors recherche */}
        {!isSearching && (
          <aside className="w-[300px] shrink-0 border-r border-zinc-200 bg-white overflow-auto app-scroll" data-testid="tree-view">
            <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono sticky top-0">
              Dossiers
            </div>
            <div className="py-1">
              {loading ? (
                <div className="p-4 text-xs text-zinc-500">Chargement…</div>
              ) : !hasContent ? (
                <div className="p-4 text-xs text-zinc-500">Aucun document classé.</div>
              ) : (
                renderTreeRows(tree, "/", 0)
              )}
            </div>
          </aside>
        )}

        {/* Colonne centrale : résultats de recherche OU panneau de fichiers */}
        <div className="flex-1 overflow-y-auto app-scroll bg-white" data-testid={isSearching ? "search-results" : "file-pane"}>
          {isSearching ? (
            <>
              <div className="grid grid-cols-[28px_minmax(0,1fr)_200px_180px_120px_100px] gap-2 items-center px-3 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500 font-mono sticky top-0">
                <span /><span>Fichier</span><span>Chemin</span><span>Type</span><span>Date</span><span>Taille</span>
              </div>
              {searching && searchResults.length === 0 ? (
                <div className="p-8 text-center text-sm text-zinc-400">Recherche…</div>
              ) : searchResults.length === 0 ? (
                <div className="p-8 text-center text-sm text-zinc-500">Aucun document trouvé.</div>
              ) : (
                searchResults.map((d) => {
                  const fname = d.classified_filename || d.original_filename;
                  return (
                    <div key={d.id} data-testid={`search-result-${d.id}`}
                      onClick={() => selectFile({ ...d, filename: fname })}
                      className={`grid grid-cols-[28px_minmax(0,1fr)_200px_180px_120px_100px] gap-2 items-center px-3 py-1.5 cursor-pointer border-b border-zinc-50 transition-colors ${selectedFile?.id === d.id ? "bg-[#cbe3ff]" : "hover:bg-[#e6f1ff]"}`}>
                      <FileText size={14} strokeWidth={1.5} className="text-[#DC2626]" />
                      <span className="truncate text-sm">{fname}</span>
                      <span className="truncate text-xs text-zinc-500 font-mono">{d.classified_path || "—"}</span>
                      <span className="truncate text-xs text-zinc-500">{d.document_type_name || "—"}</span>
                      <span className="text-xs text-zinc-400 font-mono">
                        {d.classified_at ? new Date(d.classified_at).toLocaleDateString("fr-FR") : "—"}
                      </span>
                      <span className="text-xs text-zinc-400 font-mono" data-testid={`search-size-${d.id}`}>
                        {formatFileSize(d.size_bytes)}{d.page_count != null ? ` · ${d.page_count}p` : ""}
                      </span>
                    </div>
                  );
                })
              )}
            </>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono sticky top-0 grid grid-cols-[28px_minmax(0,1fr)_180px_120px_90px] gap-2">
                <span></span>
                <span>Nom</span>
                <span>Type</span>
                <span>Modifié</span>
                <span>Taille</span>
              </div>

              {loading ? (
                <div className="p-6 text-sm text-zinc-500">Chargement…</div>
              ) : !nodeAtPath ? (
                <div className="p-6 text-sm text-zinc-500">Dossier introuvable.</div>
              ) : (
                <div className="text-[13px]">
                  {/* Sub-folders first */}
                  {subFolders.map((c) => {
                    const childPath = (selectedPath === "/" ? "/" : selectedPath) + c.name + "/";
                    return (
                      <div
                        key={`sf:${childPath}`}
                        onDoubleClick={() => { setSelectedPath(childPath); setOpenPaths((p) => new Set([...p, childPath])); }}
                        onClick={() => setSelectedPath(childPath)}
                        className="grid grid-cols-[28px_minmax(0,1fr)_180px_120px_90px] gap-2 items-center px-3 py-1.5 hover:bg-[#e6f1ff] cursor-pointer border-b border-zinc-50 transition-colors"
                        data-testid={`pane-folder-${c.name}`}
                      >
                        <Folder size={16} strokeWidth={1.5} className="text-[#FFC100] fill-[#FFE082]" />
                        <span className="truncate font-medium">{c.name}</span>
                        <span className="text-zinc-500 text-xs">Dossier</span>
                        <span className="text-zinc-400 text-xs font-mono">—</span>
                        <span className="text-zinc-400 text-xs font-mono">—</span>
                      </div>
                    );
                  })}

                  {/* Then files */}
                  {filesInCurrent.map((d) => (
                    <div
                      key={`fi:${d.id}`}
                      onClick={() => selectFile(d)}
                      className={`grid grid-cols-[28px_minmax(0,1fr)_180px_120px_90px] gap-2 items-center px-3 py-1.5 cursor-pointer border-b border-zinc-50 transition-colors ${
                        selectedFile?.id === d.id ? "bg-[#cbe3ff]" : "hover:bg-[#e6f1ff]"
                      }`}
                      data-testid={`pane-file-${d.id}`}
                    >
                      <FileText size={16} strokeWidth={1.5} className="text-[#DC2626]" />
                      <span className="truncate">{d.filename}</span>
                      <span className="text-zinc-500 text-xs truncate">{d.type || "Document PDF"}</span>
                      <span className="text-zinc-400 text-xs font-mono">
                        {d.classified_at ? new Date(d.classified_at).toLocaleDateString("fr-FR") : "—"}
                      </span>
                      <span className="text-zinc-400 text-xs font-mono" data-testid={`pane-size-${d.id}`}>
                        {formatFileSize(d.size_bytes)}{d.page_count != null ? ` · ${d.page_count}p` : ""}
                      </span>
                    </div>
                  ))}

                  {subFolders.length === 0 && filesInCurrent.length === 0 && (
                    <div className="p-12 text-center">
                      <FolderOpen size={40} strokeWidth={1} className="text-zinc-300 mx-auto" />
                      <div className="mt-3 text-sm text-zinc-500">Ce dossier est vide.</div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Colonne droite : panneau de prévisualisation — accessible en mode arbre ET en mode recherche */}
        {selectedFile && (
          <aside
            className="w-[400px] shrink-0 border-l border-zinc-200 bg-white flex flex-col overflow-hidden"
            data-testid="tree-preview-panel"
          >
            {/* Header */}
            <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-start gap-2">
              <FileText size={14} strokeWidth={1.5} className="text-[#DC2626] shrink-0 mt-0.5" />
              <span
                className="text-[12px] font-mono text-zinc-800 font-medium flex-1 min-w-0 break-all leading-tight"
                data-testid="preview-filename"
              >
                {selectedFile.filename}
              </span>
              <button
                data-testid="preview-close"
                onClick={closePreview}
                className="text-zinc-400 hover:text-zinc-700 transition-colors shrink-0"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>

            {/* Actions */}
            <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
              <button
                data-testid="preview-download"
                onClick={downloadFile}
                disabled={!signedUrl}
                className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 px-3 py-1.5 hover:bg-zinc-100 transition-colors disabled:opacity-40"
              >
                <Download size={12} strokeWidth={1.5} /> Télécharger
              </button>
              <button
                data-testid="preview-reclassify"
                onClick={startReclassify}
                className={`inline-flex items-center gap-1.5 text-xs border px-3 py-1.5 transition-colors ${
                  reclassifyMode
                    ? "border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5"
                    : "border-zinc-300 hover:bg-zinc-100"
                }`}
              >
                <RefreshCw size={12} strokeWidth={1.5} /> Reclassifier
              </button>
              <button
                data-testid="preview-delete"
                onClick={() => setDeleteTarget(selectedFile)}
                className="inline-flex items-center gap-1.5 text-xs border border-[#DC2626]/40 text-[#DC2626] px-3 py-1.5 hover:bg-[#DC2626]/5 transition-colors"
              >
                <Trash2 size={12} strokeWidth={1.5} /> Supprimer
              </button>
              <span className="ml-auto text-[10px] text-zinc-400 font-mono flex items-center gap-2">
                {selectedFile.page_count != null && (
                  <span data-testid="preview-page-count">{selectedFile.page_count} page{selectedFile.page_count > 1 ? "s" : ""}</span>
                )}
                {selectedFile.classified_at
                  ? new Date(selectedFile.classified_at).toLocaleDateString("fr-FR")
                  : "—"}
              </span>
            </div>

            {/* Petite fenêtre d'aperçu — clic pour ouvrir la lightbox */}
            <div
              data-testid="preview-pdf"
              className="mx-4 my-3 border border-zinc-200 cursor-pointer group relative overflow-hidden bg-zinc-100"
              style={{ height: "220px" }}
              onClick={() => signedUrl && setLightboxOpen(true)}
            >
              {urlLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 size={20} strokeWidth={1.5} className="animate-spin text-zinc-400" />
                </div>
              ) : signedUrl ? (
                <>
                  <iframe
                    key={signedUrl}
                    src={signedUrl}
                    title="Aperçu PDF"
                    className="w-full h-full border-0 pointer-events-none"
                    style={{ transform: "scale(1)", transformOrigin: "top left" }}
                  />
                  {/* Overlay hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 text-white text-xs font-mono px-3 py-1.5">
                      Ouvrir l'aperçu
                    </span>
                  </div>
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-zinc-400">
                  Aperçu indisponible
                </div>
              )}
            </div>
            {/* Reclassification panel */}
            {reclassifyMode && (
              <div className="px-4 py-3 border-t border-[#002FA7]/20 bg-[#002FA7]/3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-[#002FA7] font-mono font-semibold">Reclassifier</div>
                  <button
                    onClick={() => { setReclassifyMode(false); setReclassTypeId(""); setReclassTypeSearch(""); setEditMeta(docDetails?.metadata || {}); setTypeFields([]); if (docDetails?.document_type_id) { api.get(`/document-types/${docDetails.document_type_id}`).then((r) => setTypeFields(r.data.fields || [])).catch(() => {}); } }}
                    className="text-zinc-400 hover:text-zinc-700 text-[10px] font-mono"
                  >
                    Annuler
                  </button>
                </div>
                {/* Type selector */}
                <div ref={reclassTypeRef} className="relative">
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
                    Nouveau type <span className="text-[#DC2626]">*</span>
                  </label>
                  <input
                    data-testid="reclass-type-search"
                    type="text"
                    value={reclassTypeSearch}
                    onChange={(e) => { setReclassTypeSearch(e.target.value); setReclassTypeOpen(true); }}
                    onFocus={() => setReclassTypeOpen(true)}
                    placeholder="Rechercher un type…"
                    className="w-full text-sm border border-zinc-300 px-2 py-1.5 bg-white focus:outline-none focus:border-[#002FA7] font-mono"
                  />
                  {reclassTypeOpen && (
                    <ul className="absolute z-50 w-full bg-white border border-zinc-200 shadow-lg max-h-48 overflow-y-auto mt-0.5">
                      {allTypes
                        .filter((t) => !reclassTypeSearch || t.name.toLowerCase().includes(reclassTypeSearch.toLowerCase()))
                        .map((t) => (
                          <li
                            key={t.id}
                            onMouseDown={(e) => { e.preventDefault(); selectReclassType(t); }}
                            className={`px-3 py-2 text-xs cursor-pointer hover:bg-[#002FA7]/5 font-mono flex items-center gap-2 ${reclassTypeId === t.id ? "bg-[#002FA7]/10 text-[#002FA7] font-semibold" : ""}`}
                          >
                            {t.name}
                          </li>
                        ))}
                      {allTypes.filter((t) => !reclassTypeSearch || t.name.toLowerCase().includes(reclassTypeSearch.toLowerCase())).length === 0 && (
                        <li className="px-3 py-2 text-xs text-zinc-400 italic">Aucun type trouvé</li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            )}
            {/* Métadonnées éditables */}
            {/* Métadonnées éditables + bouton Enregistrer/Reclassifier */}
            {(typeFields.length > 0 || reclassifyMode || docDetails?.document_type_id) && (
              <div className="px-4 py-3 border-t border-zinc-200 space-y-3 overflow-y-auto flex-1">
                {typeFields.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">Métadonnées</div>
                    {typeFields.map((f) => (
                      <label key={f.key} className="block">
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                          {f.label || f.key}{f.required && <span className="text-[#DC2626] ml-0.5">*</span>}
                        </span>
                        {f.field_type === "societe" ? (
                          <select
                            data-testid={`edit-meta-${f.key}`}
                            value={editMeta[f.key] ?? ""}
                            onChange={(e) => setEditMeta((prev) => ({ ...prev, [f.key]: e.target.value }))}
                            className="mt-1 block w-full text-sm border border-zinc-200 px-2 py-1.5 bg-white focus:outline-none focus:border-zinc-500 cursor-pointer"
                          >
                            <option value="">— Sélectionner —</option>
                            {societes.map((s) => (
                              <option key={s.id} value={s.nom}>{s.nom}{s.siren ? ` (${s.siren})` : ""}</option>
                            ))}
                          </select>
                        ) : (f.field_type === "contact" || f.field_type === "entreprise") ? (
                          <BedeeSearchField
                            data-testid={`edit-meta-${f.key}`}
                            type={f.field_type}
                            value={editMeta[f.key] ?? ""}
                            onChange={(v) => setEditMeta((prev) => ({ ...prev, [f.key]: v }))}
                            onSelect={f.field_type === "entreprise"
                              ? (item) => setActiveEntrepriseId(item.id)
                              : undefined}
                            entrepriseId={f.field_type === "contact" ? activeEntrepriseId : undefined}
                            placeholder={f.field_type === "entreprise" ? "Rechercher une entreprise…" : "Rechercher un contact…"}
                            className="mt-1 block w-full text-sm border border-zinc-200 px-2 py-1.5 bg-white focus:outline-none focus:border-zinc-500 font-mono"
                          />
                        ) : (
                          <input
                            data-testid={`edit-meta-${f.key}`}
                            type={f.field_type === "date" ? "date" : "text"}
                            value={editMeta[f.key] ?? ""}
                            onChange={(e) => setEditMeta((prev) => ({ ...prev, [f.key]: e.target.value }))}
                            className="mt-1 block w-full text-sm border border-zinc-200 px-2 py-1.5 bg-white focus:outline-none focus:border-zinc-500 font-mono"
                          />
                        )}
                      </label>
                    ))}
                  </>
                )}
                <button
                  data-testid="save-metadata"
                  onClick={saveMetadata}
                  disabled={savingMeta || (reclassifyMode && !reclassTypeId)}
                  className="w-full flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-medium py-2 transition-colors disabled:opacity-50"
                >
                  {savingMeta && <Loader2 size={12} strokeWidth={1.5} className="animate-spin" />}
                  {savingMeta ? "Enregistrement…" : reclassifyMode ? "Reclassifier le document" : "Enregistrer et renommer"}
                </button>
                {!reclassifyMode && docDetails?.classified_path && (
                  <div className="text-[10px] font-mono text-zinc-400 break-all leading-relaxed">
                    {docDetails.classified_path}/{docDetails.classified_filename}
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>

      {/* Status bar — masqué en mode recherche */}
      {!isSearching && (
      <div className="border-t border-zinc-200 bg-zinc-50/50 px-6 py-1.5 text-[11px] text-zinc-500 font-mono flex items-center gap-4">
        <span data-testid="status-count">
          {nodeAtPath ? `${subFolders.length} dossier(s) · ${filesInCurrent.length} fichier(s)` : ""}
        </span>
        <span className="ml-auto truncate">
          {selectedFile ? `Sélection : ${selectedFile.filename}` : ""}
        </span>
      </div>
      )}

      {/* Lightbox PDF pleine page */}
      {lightboxOpen && signedUrl && (
        <div
          data-testid="pdf-lightbox"
          className="fixed inset-0 z-50 flex flex-col"
          style={{ background: "rgba(0,0,0,0.85)" }}
        >
          {/* Barre lightbox */}
          <div className="flex items-center gap-3 px-6 py-3 bg-zinc-900 border-b border-zinc-700 shrink-0">
            <FileText size={14} strokeWidth={1.5} className="text-[#DC2626] shrink-0" />
            <span className="text-[12px] font-mono text-zinc-200 flex-1 truncate">
              {selectedFile?.filename}
            </span>
            <button
              data-testid="preview-download"
              onClick={downloadFile}
              className="inline-flex items-center gap-1.5 text-xs border border-zinc-600 text-zinc-300 px-3 py-1.5 hover:bg-zinc-700 transition-colors"
            >
              <Download size={12} strokeWidth={1.5} /> Télécharger
            </button>
            <button
              data-testid="lightbox-close"
              onClick={() => setLightboxOpen(false)}
              className="text-zinc-400 hover:text-white transition-colors ml-2"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>
          {/* PDF pleine page */}
          <div className="flex-1 p-6 min-h-0">
            <iframe
              key={`lb-${signedUrl}`}
              src={signedUrl}
              title="Aperçu PDF pleine page"
              className="w-full h-full border-0 bg-white"
              data-testid="lightbox-iframe"
            />
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !deleting && setDeleteTarget(null)} />
          <div
            data-testid="delete-confirm-modal"
            className="relative bg-white border border-zinc-200 shadow-2xl w-full max-w-md mx-4"
          >
            <div className="px-6 py-4 border-b border-zinc-200 flex items-start gap-3">
              <AlertTriangle size={18} strokeWidth={1.5} className="text-[#DC2626] mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-zinc-900">Supprimer ce document ?</div>
                <div className="text-sm text-zinc-600 mt-1 break-all">
                  {deleteTarget.filename}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 text-sm text-zinc-600">
              Cette action est irréversible. Le fichier sera supprimé du stockage et de l'arborescence.
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <button
                data-testid="delete-cancel"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-100 transition-colors disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                data-testid="delete-confirm"
                onClick={confirmDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#b91c1c] text-white text-sm font-medium px-5 py-2 transition-colors disabled:opacity-50"
              >
                {deleting ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <Trash2 size={14} strokeWidth={1.5} />}
                {deleting ? "Suppression…" : "Supprimer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
